import React, { useState } from 'react';
import { ChevronDown, ChevronUp, Pencil, Trash2, Calendar, Check, RotateCcw } from 'lucide-react';
import { format, getDay } from 'date-fns';
import { parseDate } from '../../utils/dates';
import WeekendShiftIndicator from './WeekendShiftIndicator';
import './HolidayCard.css';

function HolidayCard({
  holiday,
  onToggleObserved,
  onEdit,
  onDelete,
  onAcceptShift,
  onOverrideDate,
  canManage = false,
  isAdmin = false,
  observeLimitReached = false,
}) {
  const [expanded, setExpanded] = useState(false);
  const [showDateOverride, setShowDateOverride] = useState(false);
  const [overrideDate, setOverrideDate] = useState('');

  const hd = holiday.holiday_date;
  const recurringLabel = {
    fixed: 'Fixed',
    variable: 'Variable',
    custom: 'Custom',
  }[holiday.recurring_type] || holiday.recurring_type;

  const observedDate = hd ? parseDate(hd.observed_date) : null;
  const actualDate = hd ? parseDate(hd.actual_date) : null;
  const observedDisplay = observedDate ? format(observedDate, 'EEEE, MMMM d, yyyy') : 'No date generated';
  const actualDisplay = actualDate ? format(actualDate, 'EEEE, MMMM d, yyyy') : null;

  // Check if actual and observed dates are different (weekend shift or manual override)
  const datesAreDifferent = actualDate && observedDate
    && format(actualDate, 'yyyy-MM-dd') !== format(observedDate, 'yyyy-MM-dd');

  // Check if the actual date falls on a weekend (determines if shift controls should show)
  const actualDayOfWeek = actualDate ? getDay(actualDate) : null;
  const actualIsWeekend = actualDayOfWeek === 0 || actualDayOfWeek === 6;
  const weekendLabel = actualDayOfWeek === 6 ? 'Saturday' : actualDayOfWeek === 0 ? 'Sunday' : null;

  return (
    <div className={`holiday-card ${holiday.is_observed ? 'observed' : 'not-observed'}`}>
      <div className="holiday-card-main">
        <div className="holiday-card-left">
          {canManage && (
            <label
              className={`observed-toggle${!holiday.is_observed && observeLimitReached ? ' disabled' : ''}`}
              title={
                !holiday.is_observed && observeLimitReached
                  ? 'Observed holiday limit reached — un-observe another first'
                  : holiday.is_observed ? 'Observed by FPS' : 'Not observed'
              }
            >
              <input
                type="checkbox"
                checked={holiday.is_observed}
                disabled={!holiday.is_observed && observeLimitReached}
                onChange={() => onToggleObserved(holiday.id, !holiday.is_observed)}
              />
              <span className="toggle-slider" />
            </label>
          )}
          <div className="holiday-card-info">
            <div className="holiday-card-name">
              {holiday.name}
              <span className="holiday-type-badge">{recurringLabel}</span>
            </div>
            <div className="holiday-card-date">
              {datesAreDifferent ? (
                <>
                  <span className="holiday-date-observed">Observed: {observedDisplay}</span>
                  <span className="holiday-date-actual">Actual: {actualDisplay}
                    {actualIsWeekend && <span className="weekend-badge">{weekendLabel}</span>}
                  </span>
                </>
              ) : (
                <>
                  {observedDisplay}
                  {actualIsWeekend && actualDate && (
                    <span className="weekend-badge">{weekendLabel}</span>
                  )}
                </>
              )}
            </div>
          </div>
        </div>

        <div className="holiday-card-right">
          {/* Override observed date — available for all holidays, not just weekends */}
          {canManage && hd && (
            <button
              className={`icon-btn ${showDateOverride ? 'active' : ''}`}
              onClick={() => {
                setShowDateOverride(!showDateOverride);
                // Pre-fill with the observed (or actual) date so the picker opens to the right month
                const defaultDate = hd.observed_date || hd.actual_date;
                setOverrideDate(defaultDate instanceof Date
                  ? format(parseDate(defaultDate), 'yyyy-MM-dd')
                  : String(defaultDate).substring(0, 10)
                );
              }}
              title="Change observed date"
            >
              <Calendar size={14} />
            </button>
          )}
          {/* Reset to actual date — only show when dates differ */}
          {canManage && hd && datesAreDifferent && (
            <button
              className="icon-btn"
              onClick={() => {
                const actualStr = actualDate ? format(actualDate, 'yyyy-MM-dd') : '';
                if (actualStr) {
                  onOverrideDate(hd.id, {
                    observed_date: actualStr,
                    weekend_shifted: false,
                    shift_direction: null,
                  });
                }
              }}
              title="Reset to actual date"
            >
              <RotateCcw size={14} />
            </button>
          )}
          {isAdmin && (
            <div className="holiday-card-actions">
              <button className="icon-btn" onClick={() => onEdit(holiday)} title="Edit">
                <Pencil size={14} />
              </button>
              <button className="icon-btn danger" onClick={() => onDelete(holiday)} title="Delete">
                <Trash2 size={14} />
              </button>
            </div>
          )}
          <button
            className="expand-btn"
            onClick={() => setExpanded(!expanded)}
            title={expanded ? 'Collapse' : 'Show description'}
          >
            {expanded ? <ChevronUp size={16} /> : <ChevronDown size={16} />}
          </button>
        </div>
      </div>

      {/* Date override picker — shown inline below the card header */}
      {showDateOverride && hd && (
        <div className="holiday-date-override">
          <label className="override-label">Change observed date:</label>
          <div className="override-controls">
            <input
              type="date"
              className="override-input"
              value={overrideDate}
              onChange={(e) => setOverrideDate(e.target.value)}
            />
            {overrideDate && (
              <span className="override-preview">
                {format(parseDate(overrideDate), 'EEEE, MMMM d, yyyy')}
              </span>
            )}
            <button
              className="override-apply"
              disabled={!overrideDate}
              onClick={() => {
                onOverrideDate(hd.id, {
                  observed_date: overrideDate,
                  weekend_shifted: true,
                  shift_direction: 'custom',
                });
                setShowDateOverride(false);
                setOverrideDate('');
              }}
            >
              <Check size={12} />
              Apply
            </button>
          </div>
        </div>
      )}

      {expanded && holiday.description && (
        <div className="holiday-card-description">
          {holiday.description}
        </div>
      )}
    </div>
  );
}

export default HolidayCard;
